import { useEffect, useMemo, useState } from 'react';

import { DownOutlined, RightOutlined, StarFilled } from '@ant-design/icons';
import { Button, Checkbox, Input, Modal, Select } from 'antd';
import { useTranslation } from 'react-i18next';

import { Store } from 'models/Store';

import './AddStoresModal.scss';

interface AddStoresModalProps {
  open: boolean;
  stores: Store[];
  selectedIds: number[];
  onCancel: () => void;
  onConfirm: (ids: number[]) => void;
}

interface ChainGroup {
  chain: string;
  stores: Store[];
}

function locationLabel(store: Store) {
  if (store.regionKind === 'CAPITAL') {
    return store.commune || store.regionLabel;
  }
  return store.regionLabel;
}

function AddStoresModal({
  open,
  stores,
  selectedIds,
  onCancel,
  onConfirm,
}: AddStoresModalProps) {
  const { t } = useTranslation();
  const i18nBase =
    'scenes.requirements_operation.components.new_operation_request.week_plan.picker';

  const [term, setTerm] = useState('');
  const [region, setRegion] = useState<string | null>(null);
  const [commune, setCommune] = useState<string | null>(null);
  const [chain, setChain] = useState<string | null>(null);
  const [onlyVip, setOnlyVip] = useState(false);
  const [draftIds, setDraftIds] = useState<number[]>([]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open) return;
    setDraftIds([...selectedIds]);
    setTerm('');
    setRegion(null);
    setCommune(null);
    setChain(null);
    setOnlyVip(false);
    setCollapsed({});
  }, [open, selectedIds]);

  const activeStores = useMemo(
    () => stores.filter((s) => s.isActive),
    [stores]
  );

  const regionOptions = useMemo(() => {
    const set = new Set(activeStores.map((s) => s.regionLabel));
    return [...set].sort().map((value) => ({ value, label: value }));
  }, [activeStores]);

  const communeOptions = useMemo(() => {
    const source = region
      ? activeStores.filter((s) => s.regionLabel === region)
      : activeStores;
    const set = new Set(
      source
        .map((s) => s.commune)
        .filter((c): c is string => !!c && c.trim().length > 0)
    );
    return [...set].sort().map((value) => ({ value, label: value }));
  }, [activeStores, region]);

  const chainOptions = useMemo(() => {
    const set = new Set(activeStores.map((s) => s.chain));
    return [...set].sort().map((value) => ({ value, label: value }));
  }, [activeStores]);

  const filtered = useMemo(() => {
    const q = term.trim().toLowerCase();
    return activeStores.filter((s) => {
      if (onlyVip && !s.isVip) return false;
      if (region && s.regionLabel !== region) return false;
      if (commune && s.commune !== commune) return false;
      if (chain && s.chain !== chain) return false;
      if (!q) return true;
      return [s.name, s.code, s.chain, s.commune || '', s.regionLabel]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [activeStores, term, onlyVip, region, commune, chain]);

  const groups: ChainGroup[] = useMemo(() => {
    const map = new Map<string, Store[]>();
    filtered.forEach((s) => {
      const list = map.get(s.chain) || [];
      list.push(s);
      map.set(s.chain, list);
    });
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b, 'es'))
      .map(([chainName, list]) => ({
        chain: chainName,
        stores: list.sort((a, b) => a.name.localeCompare(b.name, 'es')),
      }));
  }, [filtered]);

  const filteredIds = filtered.map((s) => s.id);
  const visibleChainCount = groups.length;
  const allVisibleSelected =
    filteredIds.length > 0 && filteredIds.every((id) => draftIds.includes(id));

  const toggleStore = (id: number, checked: boolean) => {
    setDraftIds((prev) =>
      checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)
    );
  };

  const toggleChain = (group: ChainGroup, checked: boolean) => {
    const ids = group.stores.map((s) => s.id);
    setDraftIds((prev) => {
      if (checked) return [...new Set([...prev, ...ids])];
      return prev.filter((id) => !ids.includes(id));
    });
  };

  const selectAllVisible = () => {
    setDraftIds((prev) => [...new Set([...prev, ...filteredIds])]);
  };

  const chainState = (group: ChainGroup) => {
    const ids = group.stores.map((s) => s.id);
    const selected = ids.filter((id) => draftIds.includes(id)).length;
    return {
      checked: selected === ids.length && ids.length > 0,
      indeterminate: selected > 0 && selected < ids.length,
    };
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={720}
      destroyOnClose
      className="add-stores-modal"
      title={
        <div className="add-stores-modal__title-block">
          <div className="add-stores-modal__title">
            {t(`${i18nBase}.title`)}
          </div>
          <p className="add-stores-modal__description">
            {t(`${i18nBase}.description`)}
          </p>
        </div>
      }
      footer={
        <div className="add-stores-modal__footer">
          <span className="add-stores-modal__footer-count">
            {t(`${i18nBase}.selected_count`, { count: draftIds.length })}
          </span>
          <div className="add-stores-modal__footer-actions">
            <Button onClick={onCancel}>{t(`${i18nBase}.cancel`)}</Button>
            <Button
              type="primary"
              disabled={draftIds.length === 0}
              onClick={() => onConfirm(draftIds)}
            >
              {t(`${i18nBase}.confirm`)}
            </Button>
          </div>
        </div>
      }
    >
      <div className="add-stores-modal__body">
        <Input.Search
          allowClear
          className="add-stores-modal__search"
          placeholder={t(`${i18nBase}.search`)}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />

        <div className="add-stores-modal__filters">
          <Select
            allowClear
            className="add-stores-modal__filter"
            placeholder={t(`${i18nBase}.filters.region`)}
            options={regionOptions}
            value={region}
            onChange={(value) => {
              setRegion(value || null);
              setCommune(null);
            }}
          />
          <Select
            allowClear
            className="add-stores-modal__filter"
            placeholder={t(`${i18nBase}.filters.commune`)}
            options={communeOptions}
            value={commune}
            onChange={(value) => setCommune(value || null)}
          />
          <Select
            allowClear
            className="add-stores-modal__filter"
            placeholder={t(`${i18nBase}.filters.chain`)}
            options={chainOptions}
            value={chain}
            onChange={(value) => setChain(value || null)}
          />
        </div>

        <div className="add-stores-modal__toolbar">
          <Checkbox
            checked={onlyVip}
            onChange={(e) => setOnlyVip(e.target.checked)}
          >
            {t(`${i18nBase}.only_vip`)}
          </Checkbox>
          <span className="add-stores-modal__scope">
            {t(`${i18nBase}.scope`, {
              stores: filtered.length,
              chains: visibleChainCount,
            })}
          </span>
          <Button
            size="small"
            className="add-stores-modal__select-all"
            disabled={filteredIds.length === 0 || allVisibleSelected}
            onClick={selectAllVisible}
          >
            {t(`${i18nBase}.select_all`, { count: filtered.length })}
          </Button>
        </div>

        <div className="add-stores-modal__list">
          {groups.length === 0 ? (
            <div className="add-stores-modal__empty">
              {t(`${i18nBase}.empty`)}
            </div>
          ) : (
            groups.map((group) => {
              const isCollapsed = !!collapsed[group.chain];
              const state = chainState(group);
              return (
                <div key={group.chain} className="add-stores-modal__group">
                  <div className="add-stores-modal__group-head">
                    <Checkbox
                      checked={state.checked}
                      indeterminate={state.indeterminate}
                      onChange={(e) => toggleChain(group, e.target.checked)}
                    />
                    <button
                      type="button"
                      className="add-stores-modal__group-toggle"
                      onClick={() =>
                        setCollapsed((prev) => ({
                          ...prev,
                          [group.chain]: !prev[group.chain],
                        }))
                      }
                    >
                      <span className="add-stores-modal__group-name">
                        {group.chain}
                      </span>
                      <span className="add-stores-modal__group-count">
                        {t(`${i18nBase}.chain_count`, {
                          count: group.stores.length,
                        })}
                      </span>
                      {isCollapsed ? <RightOutlined /> : <DownOutlined />}
                    </button>
                  </div>

                  {!isCollapsed && (
                    <div className="add-stores-modal__group-body">
                      {group.stores.map((store) => {
                        const checked = draftIds.includes(store.id);
                        return (
                          <label
                            key={store.id}
                            className={`add-stores-modal__store${
                              checked ? ' add-stores-modal__store--on' : ''
                            }`}
                          >
                            <Checkbox
                              checked={checked}
                              onChange={(e) =>
                                toggleStore(store.id, e.target.checked)
                              }
                            />
                            <span className="add-stores-modal__store-main">
                              <span className="add-stores-modal__store-name">
                                {store.isVip && (
                                  <StarFilled className="add-stores-modal__star" />
                                )}
                                {store.name}
                              </span>
                              <span className="add-stores-modal__store-meta">
                                {store.code} · {locationLabel(store)}
                              </span>
                            </span>
                            {store.isVip && (
                              <span className="add-stores-modal__vip-badge">
                                VIP
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}

export default AddStoresModal;
